import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import EmailSignup from "../components/EmailSignup";
import AmbientMotion from "../components/AmbientMotion";
import LeafSignature from "../components/LeafSignature";
import MarkdownContent from "../components/MarkdownContent";
import { useMeta } from "../hooks/useMeta";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Article() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useMeta({
    title: article?.title,
    description: article?.excerpt,
    image: article?.image,
    type: "article",
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    setLoading(true);
    setError(false);
    Promise.all([
      axios.get(`${API}/articles/${slug}`),
      axios.get(`${API}/articles`),
    ])
      .then(([one, all]) => {
        setArticle(one.data);
        setRelated((all.data || []).filter((a) => a.slug !== slug).slice(0, 3));
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-sm tracking-[0.25em] uppercase text-primary">
          Loading…
        </p>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-background relative">
        <AmbientMotion />
        <Header />
        <div className="max-w-2xl mx-auto px-6 py-40 text-center">
          <h1 className="font-serif text-4xl font-light">Essay not found.</h1>
          <button
            onClick={() => navigate("/")}
            data-testid="back-home-button"
            className="mt-8 text-sm uppercase tracking-[0.25em] text-primary editorial-link"
          >
            Return home
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground relative">
      <AmbientMotion />
      <Header />

      {/* Hero */}
      <article data-testid="article-page" className="pt-36 sm:pt-40 lg:pt-44">
        <div className="max-w-3xl mx-auto px-6 sm:px-8">
          <Link
            to="/#journal"
            data-testid="back-to-journal"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-primary editorial-link"
          >
            <ArrowLeft size={14} strokeWidth={1.5} />
            Back to the journal
          </Link>

          {/* Opening leaf — global template */}
          <LeafSignature variant="mark" />

          <div className="flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-primary">
            <span>{article.category}</span>
            <span className="h-px w-6 bg-border" />
            <span className="text-muted-foreground">{article.read_time}</span>
          </div>

          <h1
            data-testid="article-title"
            className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight"
          >
            {article.title}
          </h1>
          <p className="mt-6 font-serif italic text-xl sm:text-2xl text-muted-foreground leading-relaxed">
            {article.excerpt}
          </p>
        </div>

        <div className="mt-16 max-w-5xl mx-auto px-6">
          <div className="relative aspect-[16/9] sm:aspect-[16/8] rounded-[2rem] overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover ken-burns-slow"
            />
            <div className="absolute inset-0 light-sweep pointer-events-none opacity-60" />
          </div>
        </div>

        {/* Body */}
        <div className="max-w-2xl mx-auto px-6 py-20 sm:py-24">
          <MarkdownContent withDropCap className="text-lg sm:text-[1.2rem]">
            {article.content}
          </MarkdownContent>

          {/* Closing signature — global template */}
          <div className="mt-12 border-t border-border/60">
            <LeafSignature variant="full" />
          </div>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section
          data-testid="related-section"
          className="bg-secondary/30 border-y border-border/50"
        >
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-24">
            <p className="text-xs uppercase tracking-[0.3em] text-primary">
              Continue reading
            </p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-light">
              Other quiet essays.
            </h2>

            <div className="mt-12 grid md:grid-cols-3 gap-10">
              {related.map((r) => (
                <Link
                  to={`/journal/${r.slug}`}
                  key={r.slug}
                  data-testid={`related-${r.slug}`}
                  className="group block"
                >
                  <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                    <img
                      src={r.image}
                      alt={r.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
                    />
                  </div>
                  <p className="mt-5 text-xs uppercase tracking-[0.25em] text-primary">
                    {r.category}
                  </p>
                  <h3 className="mt-2 font-serif text-xl sm:text-2xl font-light leading-tight group-hover:text-primary transition-colors">
                    {r.title}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <EmailSignup />
      <Footer />
    </div>
  );
}
